import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { storageService } from '../services/storageService';
import { v4 as uuidv4 } from 'uuid';
import mockCustomersData from '../data/mockCustomers.json';
import mockTasksData from '../data/mockTasks.json';
import mockInteractionsData from '../data/mockInteractions.json';
import mockTemplatesData from '../data/mockTemplates.json';
import mockUsersData from '../data/mockUsers.json';
import { initialRolesPermissions } from '../data/mockRolesPermissions';
import { initialLookups } from '../data/initialLookups';

const DataContext = createContext(null);

const CUSTOMERS_KEY = 'crm_customers';
const TASKS_KEY = 'crm_tasks';
const INTERACTIONS_KEY = 'crm_interactions';
const TEMPLATES_KEY = 'crm_templates';
const USERS_KEY = 'crm_users';
const ROLES_PERMISSIONS_KEY = 'crm_roles_permissions';
const LOOKUPS_KEY = 'crm_lookups';

export const DataProvider = ({ children }) => {
    const [customers, setCustomers] = useState(() => storageService.getItems(CUSTOMERS_KEY, mockCustomersData));
    const [tasks, setTasks] = useState(() => storageService.getItems(TASKS_KEY, mockTasksData));
    const [interactions, setInteractions] = useState(() => storageService.getItems(INTERACTIONS_KEY, mockInteractionsData));
    const [templates] = useState(() => storageService.getItems(TEMPLATES_KEY, mockTemplatesData));
    const [users, setUsers] = useState(() => storageService.getItems(USERS_KEY, mockUsersData));
    const [rolesPermissionsMap, setRolesPermissionsMap] = useState(() => {
        const storedMap = storageService.getItems(ROLES_PERMISSIONS_KEY, initialRolesPermissions);
        console.log("DataContext Init: rolesPermissionsMap loaded:", storedMap);
        return storedMap;
    });
    const [roles, setRoles] = useState(() => Object.keys(rolesPermissionsMap));
    const [lookups, setLookups] = useState(() => storageService.getItems(LOOKUPS_KEY, initialLookups));
    const [loading, setLoading] = useState(false);

    useEffect(() => { setRoles(Object.keys(rolesPermissionsMap)); }, [rolesPermissionsMap]);
    
    const refreshData = useCallback(() => {
        setLoading(true);
        setCustomers(storageService.getItems(CUSTOMERS_KEY));
        setTasks(storageService.getItems(TASKS_KEY));
        setInteractions(storageService.getItems(INTERACTIONS_KEY));
        setUsers(storageService.getItems(USERS_KEY));
        const refreshedRolesMap = storageService.getItems(ROLES_PERMISSIONS_KEY, initialRolesPermissions);
        setRolesPermissionsMap(refreshedRolesMap);
        setLookups(storageService.getItems(LOOKUPS_KEY, initialLookups));
        setLoading(false);
    }, []);


    // --- Customer CRUD ---
    const addCustomer = (customerData) => {
        const newCustomer = storageService.addItem(CUSTOMERS_KEY, customerData);
        setCustomers(prev => [...prev, newCustomer]);
        return newCustomer;
    };
    const updateCustomer = (id, customerData) => {
        const updated = storageService.updateItem(CUSTOMERS_KEY, id, customerData);
        setCustomers(prev => prev.map(c => c.id === id ? updated : c));
        return updated;
    };
    const deleteCustomer = (id) => {
        storageService.deleteItem(CUSTOMERS_KEY, id);
        setCustomers(prev => prev.filter(c => c.id !== id));
    };
    const getCustomerById = (id) => storageService.getItemById(CUSTOMERS_KEY, id);

    // --- Task CRUD ---
    const addTask = (taskData) => {
        const newTask = storageService.addItem(TASKS_KEY, taskData);
        setTasks(prev => [...prev, newTask]);
        return newTask;
    };
     const updateTask = (id, taskData) => {
        const updated = storageService.updateItem(TASKS_KEY, id, taskData);
        setTasks(prev => prev.map(t => t.id === id ? updated : t));
         if (taskData.status && taskData.status === 'Completed') {
            addInteraction({
                customerId: updated.customerId, channel: 'System', date: new Date().toISOString(),
                userId: 'system', notes: `Task '${updated.description?.substring(0, 20)}...' marked as Completed.`,
             });
         }
        return updated;
    };
    const deleteTask = (id) => {
        storageService.deleteItem(TASKS_KEY, id);
        setTasks(prev => prev.filter(t => t.id !== id));
    };
    const getTaskById = (id) => storageService.getItemById(TASKS_KEY, id);

    // --- Interaction CRUD ---
    const addInteraction = (interactionData) => {
        const newInteraction = storageService.addItem(INTERACTIONS_KEY, interactionData);
        setInteractions(prev => [...prev, newInteraction]);
        return newInteraction;
    };
    const getInteractionById = (id) => storageService.getItemById(INTERACTIONS_KEY, id);

    // --- Merge Customers ---
    const mergeCustomers = (primaryCustomerId, secondaryCustomerIds) => {
        setLoading(true);
        try {
            const primaryCustomer = getCustomerById(primaryCustomerId);
            if (!primaryCustomer) throw new Error("Primary customer not found");

            let mergedData = { ...primaryCustomer };
            let relatedItemsToUpdate = { tasks: [], interactions: [] };

            secondaryCustomerIds.forEach(secId => {
                const secondaryCustomer = getCustomerById(secId);
                if (secondaryCustomer) {
                    // Simple merge: Overwrite empty fields in primary with secondary data
                    // More complex logic can be added here based on UI selection
                    Object.keys(secondaryCustomer).forEach(key => {
                        if (!mergedData[key] && secondaryCustomer[key]) {
                            mergedData[key] = secondaryCustomer[key];
                        }
                    });
                    // Find related items to re-assign
                    relatedItemsToUpdate.tasks.push(...tasks.filter(t => t.customerId === secId));
                    relatedItemsToUpdate.interactions.push(...interactions.filter(i => i.customerId === secId));
                }
            });

            // Update primary customer
            updateCustomer(primaryCustomerId, mergedData);

            // Re-assign related tasks and interactions
            relatedItemsToUpdate.tasks.forEach(task => updateTask(task.id, { customerId: primaryCustomerId }));
            relatedItemsToUpdate.interactions.forEach(interaction => {
                // Interactions are often immutable logs, maybe just add a note instead of updating?
                // Or update if your model allows: storageService.updateItem(INTERACTIONS_KEY, interaction.id, { customerId: primaryCustomerId });
                // For simplicity here, we'll add a note to the primary customer log
                addInteraction({
                    customerId: primaryCustomerId,
                    channel: 'System',
                    date: new Date().toISOString(),
                    userId: 'system',
                    notes: `Merged data from customer ID ${interaction.customerId}. Original interaction: ${interaction.channel} on ${new Date(interaction.date).toLocaleDateString()}`,
                });
             });


            // Delete secondary customers
            secondaryCustomerIds.forEach(secId => {
                deleteCustomer(secId); // This already filters state
            });

            refreshData(); // Refresh all data states after merge
        } catch (error) {
            console.error("Error merging customers:", error);
            // Handle error display to user
        } finally {
            setLoading(false);
        }
    };

    const addUser = (userData) => {
        console.warn("Saving user with plain text password (for mock only)");
        const newUser = storageService.addItem(USERS_KEY, userData);
        setUsers(prev => [...prev, newUser]);
        return newUser;
    };
    const updateUser = (id, updatedUserData) => {
        const currentUserData = getUserById(id);
        let dataToSave = { ...updatedUserData };
        if (updatedUserData.password && updatedUserData.password !== currentUserData.password) {
             console.warn("Updating user password with plain text (for mock only)");
             dataToSave.password = updatedUserData.password;
        } else {
            dataToSave.password = currentUserData.password;
        }
        const updated = storageService.updateItem(USERS_KEY, id, dataToSave);
        setUsers(prev => prev.map(u => u.id === id ? updated : u));
        return updated;
    };
    const deleteUser = (id) => {
        storageService.deleteItem(USERS_KEY, id);
        setUsers(prev => prev.filter(u => u.id !== id));
    };
    const getUserById = (id) => storageService.getItemById(USERS_KEY, id);


    const addRole = (roleName, permissions = []) => {
        if (rolesPermissionsMap[roleName]) { throw new Error(`Role "${roleName}" already exists.`); }
        const updatedMap = { ...rolesPermissionsMap, [roleName]: permissions };
        setRolesPermissionsMap(updatedMap);
        localStorage.setItem(ROLES_PERMISSIONS_KEY, JSON.stringify(updatedMap));
        return { roleName, permissions };
    };
    const updateRolePermissions = (roleName, permissions) => {
         if (!rolesPermissionsMap[roleName]) { throw new Error(`Role "${roleName}" not found.`); }
         const updatedMap = { ...rolesPermissionsMap, [roleName]: permissions };
         setRolesPermissionsMap(updatedMap);
         localStorage.setItem(ROLES_PERMISSIONS_KEY, JSON.stringify(updatedMap));
         return { roleName, permissions };
    };
    const deleteRole = (roleName) => {
        const isRoleAssigned = users.some(user => user.role === roleName);
        if (isRoleAssigned) { throw new Error(`Cannot delete role "${roleName}" as it is assigned to users. Please reassign users first.`); }
        const { [roleName]: _, ...remainingMap } = rolesPermissionsMap;
        setRolesPermissionsMap(remainingMap);
        localStorage.setItem(ROLES_PERMISSIONS_KEY, JSON.stringify(remainingMap));
    };
    const getPermissionsForRole = (roleName) => rolesPermissionsMap[roleName] || [];

    const addLookupGroup = (groupCode, groupLabels) => {
        if (!groupCode || !/^[A-Z0-9_]+$/.test(groupCode)) { // Basic validation for code format (uppercase, numbers, underscore)
            throw new Error("Invalid group code format. Use uppercase letters, numbers, and underscores only.");
        }
        if (lookups[groupCode]) {
            throw new Error(`Lookup Group Code "${groupCode}" already exists.`);
        }
        const newGroup = {
            group_label_en: groupLabels.label_en || groupCode, // Default label to code
            group_label_ar: groupLabels.label_ar || groupCode,
            isSystem: false, // New groups are not system groups by default
            values: []
        };
        const updatedLookups = { ...lookups, [groupCode]: newGroup };
        setLookups(updatedLookups);
        localStorage.setItem(LOOKUPS_KEY, JSON.stringify(updatedLookups));
        return newGroup;
    };

    const updateLookupGroup = (groupCode, groupLabels) => {
        const group = lookups[groupCode];
        if (!group) {
            throw new Error(`Lookup Group "${groupCode}" not found.`);
        }
        const updatedGroup = {
            ...group,
            group_label_en: groupLabels.label_en || group.group_label_en,
            group_label_ar: groupLabels.label_ar || group.group_label_ar,
        };
        const updatedLookups = { ...lookups, [groupCode]: updatedGroup };
        setLookups(updatedLookups);
        localStorage.setItem(LOOKUPS_KEY, JSON.stringify(updatedLookups));
        return updatedGroup;
    };

    const deleteLookupGroup = (groupCode) => {
        const group = lookups[groupCode];
        if (!group) {
            throw new Error(`Lookup Group "${groupCode}" not found.`);
        }
        if (group.isSystem) {
            throw new Error(`Cannot delete system Lookup Group "${groupCode}".`);
        }
        // Optional: Add check if values exist and warn/prevent?
        // if (group.values && group.values.length > 0) {
        //     throw new Error(`Cannot delete group "${groupCode}" as it contains values.`);
        // }
        const { [groupCode]: _, ...remainingLookups } = lookups;
        setLookups(remainingLookups);
        localStorage.setItem(LOOKUPS_KEY, JSON.stringify(remainingLookups));
    };

    // -- Value CRUD --
    const addLookupValue = (groupCode, valueData) => {
        const group = lookups[groupCode];
        if (!group) {
            throw new Error(`Lookup Group "${groupCode}" not found.`);
        }
         // Optional: Check for duplicate value within the group
         // if (group.values.some(v => v.value === valueData.value)) {
         //    throw new Error(`Value "${valueData.value}" already exists in group "${groupCode}".`);
         // }

        const newValue = {
            id: uuidv4(), // Generate unique ID
            value: valueData.value || '',
            label_en: valueData.label_en || valueData.value, // Default label to value
            label_ar: valueData.label_ar || valueData.value,
            isActive: valueData.isActive !== undefined ? valueData.isActive : true, // Default to active
            order: parseInt(valueData.order, 10) || (group.values.length + 1) * 10, // Default order
        };
        const updatedGroupValues = [...group.values, newValue];
        const updatedGroup = { ...group, values: updatedGroupValues };
        const updatedLookups = { ...lookups, [groupCode]: updatedGroup };
        setLookups(updatedLookups);
        localStorage.setItem(LOOKUPS_KEY, JSON.stringify(updatedLookups));
        return newValue;
    };

     const updateLookupValue = (groupCode, valueId, updatedValueData) => {
        const group = lookups[groupCode];
        if (!group) throw new Error(`Lookup Group "${groupCode}" not found.`);

        let valueFound = false;
        const updatedGroupValues = group.values.map(value => {
            if (value.id === valueId) {
                valueFound = true;
                return {
                    ...value,
                    ...updatedValueData, // Overwrite with new data
                    // Ensure boolean and number types are correct
                    isActive: updatedValueData.isActive !== undefined ? updatedValueData.isActive : value.isActive,
                    order: updatedValueData.order !== undefined ? parseInt(updatedValueData.order, 10) : value.order,
                };
            }
            return value;
        });

        if (!valueFound) throw new Error(`Lookup Value with ID "${valueId}" not found in group "${groupCode}".`);

        const updatedGroup = { ...group, values: updatedGroupValues };
        const updatedLookups = { ...lookups, [groupCode]: updatedGroup };
        setLookups(updatedLookups);
        localStorage.setItem(LOOKUPS_KEY, JSON.stringify(updatedLookups));
        return updatedLookups[groupCode].values.find(v => v.id === valueId); // Return the updated value object
    };

     const deleteLookupValue = (groupCode, valueId) => {
        const group = lookups[groupCode];
        if (!group) throw new Error(`Lookup Group "${groupCode}" not found.`);

        const updatedGroupValues = group.values.filter(value => value.id !== valueId);

        if (updatedGroupValues.length === group.values.length) {
            console.warn(`Lookup Value with ID "${valueId}" not found for deletion in group "${groupCode}".`);
            // Optionally throw an error or just return
            // throw new Error(`Lookup Value with ID "${valueId}" not found.`);
            return; // No change needed
        }

        // Add check: Prevent deleting value if it's currently used? (Complex check, skip for now)

        const updatedGroup = { ...group, values: updatedGroupValues };
        const updatedLookups = { ...lookups, [groupCode]: updatedGroup };
        setLookups(updatedLookups);
        localStorage.setItem(LOOKUPS_KEY, JSON.stringify(updatedLookups));
    };

    // -- Helper Functions --
    const getLookupGroup = (groupCode) => {
        return lookups[groupCode];
    };

    // Gets active values, sorted by 'order', ready for dropdowns
    const getLookupValues = (groupCode, activeOnly = true) => {
        const group = lookups[groupCode];
        if (!group || !group.values) return [];

        const filteredValues = activeOnly
            ? group.values.filter(v => v.isActive)
            : group.values;

        // Sort by order, then by label_en as fallback
        return filteredValues.sort((a, b) => {
            const orderA = a.order ?? Infinity;
            const orderB = b.order ?? Infinity;
            if (orderA !== orderB) {
                return orderA - orderB;
            }
            return (a.label_en || '').localeCompare(b.label_en || '');
        });
    };

    
    // **NEW:** Get List of All Lookup Groups (as an array of objects)
    const getLookupGroupsList = useCallback(() => {
        return Object.entries(lookups).map(([code, groupData]) => ({
            code: code, // Add the code as a property
            ...groupData
        }));
    }, [lookups]); // Depends only on the lookups state

    // Get details for a single group (renamed for clarity, same logic)
    const getLookupGroupDetails = useCallback((groupCode) => {
        return lookups[groupCode];
    }, [lookups]);

    // Get VALUES for a specific group (sorted, optionally filtered for active) - Keep this
    /* const getLookupValues = useCallback((groupCode, activeOnly = true) => {
        const group = lookups[groupCode];
        if (!group || !group.values) return [];
        const filteredValues = activeOnly ? group.values.filter(v => v.isActive) : group.values;
        return filteredValues.sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || (a.label_en || '').localeCompare(b.label_en || ''));
    }, [lookups]); */

    // **NEW:** Get ALL raw values for a group (unsorted, unfiltered)
    const getAllLookupValuesForGroup = useCallback((groupCode) => {
         const group = lookups[groupCode];
         return group?.values || []; // Return the raw array or empty if not found
    }, [lookups]);


    const value = {
        customers, tasks, interactions, templates, users, roles, rolesPermissionsMap, lookups,
        loading, refreshData,
        addCustomer, updateCustomer, deleteCustomer, getCustomerById, mergeCustomers,
        addTask, updateTask, deleteTask, getTaskById,
        addInteraction, getInteractionById,
        addUser, updateUser, deleteUser, getUserById,
        addRole, updateRolePermissions, deleteRole, getPermissionsForRole,
        addLookupGroup, updateLookupGroup, deleteLookupGroup,
        addLookupValue, updateLookupValue, deleteLookupValue,getLookupGroup,
        getLookupGroupsList, getLookupGroupDetails, getLookupValues, getAllLookupValuesForGroup
        /* customers, tasks, interactions, templates, users, roles, rolesPermissionsMap, lookups,
        loading, refreshData,
        // All CRUD functions...
        addCustomer, updateCustomer, deleteCustomer, getCustomerById, mergeCustomers,
        addTask, updateTask, deleteTask, getTaskById,
        addInteraction, getInteractionById,
        addUser, updateUser, deleteUser, getUserById,
        addRole, updateRolePermissions, deleteRole, getPermissionsForRole,getLookupGroup ,
        addLookupGroup, updateLookupGroup, deleteLookupGroup,
        addLookupValue, updateLookupValue, deleteLookupValue,
        getLookupGroupsList, getLookupGroupDetails, getLookupValues, getAllLookupValuesForGroup */
    };

    return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => useContext(DataContext);